import { Button, Checkbox, Group, Modal, Text, Title } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import * as React from "react";
import * as XLSX from "xlsx";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import type { ScannedSerialRow } from "../types";

/** Mở hộp chọn tệp Excel; đọc xong thì trả workbook */
export const pickExcelWorkbook = (onLoaded: (workbook: XLSX.WorkBook) => void) => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xlsx, .xls";
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) {
      NotificationExtension.Warn("Không có tập tin nào được chọn");
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        onLoaded(XLSX.read(event.target?.result, { type: "array" }));
      } catch (error) {
        console.error(error);
        NotificationExtension.Warn("Không đọc được tệp Excel");
      }
    };
    reader.readAsArrayBuffer(file);
  };
  input.click();
};

/** Đọc cột "Serial" của sheet đã chọn */
export const readSerialsFromSheet = (workbook: XLSX.WorkBook, sheetName: string) => {
  const rows: { Serial?: string | number }[] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: false });
  return rows.map((row) => (row.Serial !== undefined ? String(row.Serial) : "")).filter((s) => s.trim() !== "");
};

/** Xuất danh sách serial đã quét ra Excel */
export const exportSerialsToExcel = (rows: ScannedSerialRow[]) => {
  if (!rows.length) {
    NotificationExtension.Fails("Không có dữ liệu để xuất!");
    return;
  }
  const header = [
    "Mã vị trí",
    "Tên vị trí",
    "Mã hàng",
    "Serial",
    "Tên sản phẩm",
    "ĐVT1",
    "SL1",
    "Giá bán",
    "SL2",
    "ĐVT2",
    "Thành tiền",
    "Tình trạng",
    "Ghi chú",
  ];
  const data = rows.map((row) => [
    row.positionCode,
    row.positionName,
    row.itemCode,
    row.serial,
    row.itemName,
    row.primaryUomCode,
    row.primaryQuantity,
    row.soPrice ? row.soPrice.toLocaleString() : "",
    row.subQuantity ?? "",
    row.subUomName ?? "",
    row.soPrice ? (Number(row.soPrice) * (row.primaryQuantity || 0)).toLocaleString() : "",
    row.status ?? "",
    row.note ?? "",
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...data]), "Phiếu xuất kho");
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}-${pad(
    now.getMinutes(),
  )}-${pad(now.getSeconds())}`;
  XLSX.writeFile(workbook, `Phieu_xuat_kho_${stamp}.xlsx`);
};

interface ExcelSheetModalProps {
  workbook: XLSX.WorkBook | null;
  onClose: () => void;
  onConfirm: (serials: string[]) => void;
}

/** Chọn một sheet trong tệp Excel để import serial */
const ExcelSheetModal = ({ workbook, onClose, onConfirm }: ExcelSheetModalProps) => {
  const [selected, setSelected] = React.useState<string | null>(null);

  React.useEffect(() => setSelected(null), [workbook]);

  return (
    <Modal opened={!!workbook} onClose={onClose} title={<Title order={4}>Chọn sheet Excel</Title>}>
      {workbook?.SheetNames.map((sheet) => (
        <Checkbox
          mt={15}
          key={sheet}
          label={<Text fw={500}>{sheet}</Text>}
          checked={selected === sheet}
          onChange={(e) => setSelected(e.currentTarget.checked ? sheet : null)}
        />
      ))}
      <Group mt={15} justify="flex-end">
        <Button
          leftSection={<IconCheck size={16} />}
          disabled={!selected}
          onClick={() => {
            if (!workbook || !selected) return;
            onConfirm(readSerialsFromSheet(workbook, selected));
            onClose();
          }}
        >
          Xác nhận
        </Button>
      </Group>
    </Modal>
  );
};

export default ExcelSheetModal;
